import React, { useEffect, useRef } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import { pickDefaultVideoQuality, resolvePreferredVideoHeight } from "../../Services/MediaAssetService";
import "./VideoPlayer.scss";

const DEFAULT_SOURCE_MIME_TYPE = "video/mp4";

export interface VideoPlayerSource {
    src: string;
    /** Pixel height — doubles as the value Plyr's quality menu shows (e.g. 1080 -> "1080p"). */
    size: number;
    /** MIME type of the file behind `src`; mp4 (what the transcoder produces) when omitted. */
    type?: string;
}

interface VideoPlayerProps {
    sources: VideoPlayerSource[];
    style?: React.CSSProperties;
    autoplay?: boolean;
    /** When set, start at this height (or the next smaller rendition). When omitted, the player
     *  estimates network speed and picks from the available renditions. */
    preferredQuality?: number;
    /** Still shown by Plyr until playback starts (same image as the deferred play poster). */
    poster?: string;
}

/**
 * Wraps Plyr (https://plyr.io) instead of a bare native <video> — gives us a real, skinnable quality
 * menu inside the player's own settings (gear) button, matching how a native player would present it,
 * rather than a bolted-on overlay control. We serve separate signed URLs per resolution (no HLS/DASH),
 * which is exactly the "multiple sources with a size" shape Plyr's quality selector is built for.
 *
 * Starting quality is chosen from the browser's network estimate (see resolvePreferredVideoHeight).
 * The native element uses preload="none" so the browser only range-requests (buffers) after play,
 * instead of pulling the whole MP4 as soon as the player mounts.
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({ sources, style, autoplay = false, preferredQuality, poster }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<Plyr | null>(null);

    useEffect(() => {
        let cancelled = false;
        let player: Plyr | null = null;

        const setup = async () => {
            if (!videoRef.current || sources.length === 0) {
                return;
            }
            const sizes = Array.from(new Set(sources.map((source) => source.size))).sort((a, b) => b - a);
            const probeUrl = [...sources].sort((a, b) => a.size - b.size)[0]?.src;
            const defaultSize =
                preferredQuality === undefined
                    ? await resolvePreferredVideoHeight(sizes, probeUrl)
                    : pickDefaultVideoQuality(sizes, preferredQuality);
            if (cancelled || !videoRef.current) {
                return;
            }
            player = new Plyr(videoRef.current, {
                controls: ["play-large", "play", "progress", "current-time", "mute", "volume", "settings", "pip", "fullscreen"],
                settings: ["quality", "speed"],
                autoplay,
                poster,
                storage: { enabled: false },
                quality: {
                    default: defaultSize,
                    options: sizes,
                    forced: true,
                },
            });
            // Put the default rendition first so the browser's initial <source> pick (before Plyr's quality
            // switch) is already the network-chosen stream, not the original file.
            const orderedSources = [...sources].sort((a, b) => {
                if (a.size === defaultSize) {
                    return -1;
                }
                if (b.size === defaultSize) {
                    return 1;
                }
                return b.size - a.size;
            });
            player.source = {
                type: "video",
                sources: orderedSources.map((source) => ({
                    src: source.src,
                    type: source.type ?? DEFAULT_SOURCE_MIME_TYPE,
                    size: source.size,
                })),
                poster,
            };
            if (poster) {
                player.poster = poster;
            }
            videoRef.current.preload = "none";
            if (autoplay) {
                const readyPlayer = player;
                readyPlayer.on("ready", () => {
                    void readyPlayer.play();
                });
            }
            playerRef.current = player;
        };

        void setup();
        return () => {
            cancelled = true;
            player?.destroy();
            playerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sources]);

    return (
        <div className="knowledge-video-player" style={style}>
            <video ref={videoRef} playsInline preload="none" poster={poster} />
        </div>
    );
};

export default VideoPlayer;
